"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleAlert as AlertCircle, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
const inspectionChecklist = [{
  id: "patches",
  label: "Visible patches"
}, {
  id: "seams",
  label: "Seam repairs"
}, {
  id: "wear",
  label: "Worn areas"
}, {
  id: "discoloration",
  label: "Discoloration"
}, {
  id: "replacements",
  label: "Previous replacement sections"
}, {
  id: "blower",
  label: "Blower compatibility"
}, {
  id: "accessories",
  label: "Included accessories"
}];
export default function InflatableInspection() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="inflatableinspectionguide" data-nav="light" className="relative bg-background py-16 px-4 sm:px-6 lg:px-8 border-b border-border">
      <div className="mx-auto max-w-4xl">
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="relative border border-border bg-card p-6 sm:p-10 shadow-xs">
          {/* Industrial Datum Corner Indicators */}
          <div className="absolute -top-px -left-px font-mono text-[10px] leading-none text-muted-foreground select-none p-1 pointer-events-none">
            +
          </div>
          <div className="absolute -top-px -right-px font-mono text-[10px] leading-none text-muted-foreground select-none p-1 pointer-events-none">
            +
          </div>
          <div className="absolute -bottom-px -left-px font-mono text-[10px] leading-none text-muted-foreground select-none p-1 pointer-events-none">
            +
          </div>
          <div className="absolute -bottom-px -right-px font-mono text-[10px] leading-none text-muted-foreground select-none p-1 pointer-events-none">
            +
          </div>

          {/* Section Header */}
          <motion.header variants={itemVariants} className="space-y-2">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold uppercase tracking-tight text-foreground">
              Look at the Inflatable Separately
            </h2>
          </motion.header>

          {/* Primary Statement */}
          <motion.div variants={itemVariants} className="mt-6">
            <p className="text-base sm:text-lg text-foreground/90 leading-relaxed font-sans">
              The inflatable safety area deserves its own review because its condition can differ from the mechanical equipment.
            </p>
          </motion.div>

          {/* Structured Inspection Criteria Checklist Block */}
          <motion.div variants={itemVariants} className="mt-6 border border-border bg-muted/40 p-5 sm:p-6">
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              Check for available information about visible patches, seam repairs, worn areas, discoloration, previous replacement sections, blower compatibility and included accessories.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-2">
              {inspectionChecklist.map((item, index) => <div key={item.id} data-index={index} className="flex items-center gap-2.5 text-xs sm:text-sm font-mono text-foreground/90 bg-background/60 px-3 py-2 border border-border/50">
                  <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                  <span>{item.label}</span>
                </div>)}
            </div>
          </motion.div>

          {/* Condition & Disclosure Advisory Callout */}
          <motion.div variants={itemVariants} className="mt-6 border-l-2 border-primary border-y border-r border-border bg-card p-5">
            <div className="flex items-start gap-3">
              <AlertCircle className="size-4 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Cosmetic marks do not necessarily tell you everything about an inflatable, but buyers should know about visible wear before making a decision. When exact condition information is unavailable, the listing should make that clear.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Cross-reference Link */}
          <motion.div variants={itemVariants} className="mt-6">
            <p className="text-sm text-muted-foreground leading-relaxed font-sans">
              If you are comparing complete bull-and-inflatable systems, our{" "}
              <Link to="/InflatableMechanicalBullsPage" className="font-medium text-foreground underline underline-offset-4 decoration-primary hover:text-primary transition-colors">
                Inflatable Mechanical Bulls
              </Link>{" "}
              page explains package contents, blower requirements, replacement considerations and setup in more detail.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}